import { TableColumn } from 'react-data-table-component';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'client/_redux/reducers';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import React, { useRef, useCallback, useState } from 'react';
import { CRUDDataTableHandle } from 'dashboard/containers/CRUDDataTable';
import { useTableColumnsWithActions } from 'dashboard/containers/CRUDDataTable/useTableColumnsWithActions';
import { useNavigate } from 'react-router-dom';
import { ICategoryTour } from 'client/models/ICategoryTour';
import { createCategoryTourDashboard, getAllCategoryToursDashboard } from 'dashboard/_redux/actions/categoryTours';

export const useTourCategoriesList = () => {
  const [t] = useTranslation();
  const dataTableRef = useRef<CRUDDataTableHandle>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { categoryTours, loaded } = useSelector((state: RootState) => state.dashboardCategoryTours);

  // État pour le modal de création
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  // Ouvrir le modal de création
  const handleOpenCreateModal = useCallback(() => {
    setFormData({ name: '', description: '' });
    setIsCreateModalOpen(true);
  }, []);

  // Fermer le modal de création
  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setFormData({ name: '', description: '' });
  }, []);

  // Gérer les changements dans le formulaire
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Soumettre le formulaire de création
  const handleSubmitCreate = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    dispatch(createCategoryTourDashboard(formData));
    handleCloseCreateModal();
    // Rafraîchir la liste après création
    setTimeout(() => {
      dataTableRef.current?.getData();
    }, 500);
  }, [dispatch, formData, handleCloseCreateModal]);

  const onPressShow = useCallback(
    (item: ICategoryTour) => {
      navigate(`/dashboard/category-tour/${item._id}`);
    },
    [navigate],
  );

  const tableCols: TableColumn<ICategoryTour>[] = [
    {
      name: t('dashboard.categoryTourPage.name') || 'Nom',
      selector: (e) => e.name,
      sortable: true,
      center: true,
      sortField: 'name',
      grow: 2,
    },
    {
      name: t('dashboard.categoryTourPage.slug') || 'Slug',
      selector: (e) => e.slug,
      sortable: true,
      center: true,
      sortField: 'slug',
      grow: 1,
    },
    {
      name: t('dashboard.categoryTourPage.description') || 'Description',
      selector: (e) => e.description,
      sortable: false,
      center: true,
      wrap: true,
      grow: 3,
    },
    {
      name: t('dashboard.categoryTourPage.addedBy') || 'Créé par',
      selector: (e) => e.addedBy.username,
      sortable: true,
      center: true,
      sortField: 'addedBy.username',
      grow: 1,
    },
    {
      sortField: 'createdAt',
      name: t('dashboard.categoryTourPage.createdAt') || 'Date de création',
      selector: (e) => (e.createdAt ? format(new Date(e.createdAt), 'dd-MM-yyyy') : ''),
      sortable: true,
      center: true,
      grow: 1,
    },
  ];

  const columns = useTableColumnsWithActions({
    columns: tableCols,
    buttons: [
      {
        fn: onPressShow,
        title: t('dataTable.show') || 'Voir',
        className: 'btn btn-dark btn-xs m-1',
      },
    ],
  });

  return {
    categoryTours,
    loaded,
    tableCols,
    columns,
    dataTableRef,
    isCreateModalOpen,
    formData,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleInputChange,
    handleSubmitCreate,
  };
};
